import { COPY, fill } from "../copy.js";
import { Creature } from "../creature.js";
import { fa } from "../format.js";
import { Passage } from "../rich-text.js";
import { featuredShelfEntries, shelfEntries } from "./shelf.js";
import type { Route } from "../route.js";
import { ACCENT, familyClasses, familyLook, type AccentName } from "../accent.js";

/**
 * The landing page.
 *
 * **Mobile-first, and literally so:** every class below is the phone layout, and `md:` widens it.
 * Almost every parent arrives here on a phone, and a desktop layout narrowed down is how a page
 * ends up with cramped padding and headlines that wrap in the wrong places. Where a breakpoint can
 * be avoided entirely — type size, the games grid — it is, with `clamp()` and `auto-fill`.
 *
 * **What the previous version was missing: the product.** A parent could read the whole page and
 * still not know what their child would see. The products this competes with internationally all
 * lead with their content, and the games shelf is the one piece of that playbook we can copy
 * honestly. Their other trust devices are award badges, star ratings, testimonials and download
 * counts; we have none of those, and manufacturing them would be exactly the dishonesty this
 * product exists to refuse. What stands in their place is a section of promises a parent can check
 * for themselves in ten minutes.
 *
 * **The honesty section stays high on the page**, not in the footer. In a market that sells
 * confident verdicts from a fingerprint scan, being the product that says what it cannot tell you
 * is the whole differentiator. If it costs conversions, that IS the H1 result, and it is worth
 * knowing before the money is spent rather than after.
 *
 * Copy stays in one key per section because H1 is a two-variant test at a real price: a second
 * variant should be a copy change, not a rewrite.
 *
 * **Tailwind for layout, `theme.ts` for anything colour-derived.** The tinted shadows below are
 * computed per accent, and expressing that as a utility needs `color-mix`, which is too new for
 * the WebView versions this has to run on (ADR-007). The palette itself is not duplicated: the
 * utility classes are generated from the same file these inline values come from.
 */
export function LandingPage({
  navigate,
  home,
}: {
  navigate: (route: Route) => void;
  /** The signed-in visitor's own surface, or null. See `SiteHeader`. */
  home: Route | null;
}) {
  const start = () => navigate(home ?? { kind: "enter" });
  /**
   * The second door, and null once somebody is signed in.
   *
   * The main call to action goes to sign-in, which is the right destination for a teacher who has an
   * account and a dead end for one who does not — the sign-in screen no longer offers a way to register
   * (ADR-012). So the offer is made twice on the page a teacher is reading anyway, and to nobody who
   * already has a class waiting for them.
   */
  const apply = home === null ? () => navigate({ kind: "apply" }) : null;

  return (
    <>
      <Hero onStart={start} onAbout={() => navigate({ kind: "about" })} onApply={apply} />
      <GameShelf />
      <Values />
      <Honesty />
      <Promises />
      <HowItWorks />
      <Closing onStart={start} onApply={apply} />
    </>
  );
}

/**
 * "No account yet? Register" — the line under both calls to action.
 *
 * A sentence and a link rather than a third button. Two filled buttons and a third would leave nothing
 * looking like the primary action, and the person this is for is reading the page rather than scanning
 * it for controls.
 */
function ApplyPrompt({ onApply }: { onApply: () => void }) {
  return (
    // `text-body` rather than `text-small`: the header now carries this link too, but here it sits
    // right under the primary button at the exact moment someone without an account hits a dead end,
    // and a 15px underlined word at that moment reads as a footnote rather than a way forward.
    <p className="mt-lg text-body text-muted">
      {COPY.site.teacherPrompt}{" "}
      <button onClick={onApply} className="min-h-11 font-bold text-grape underline">
        {COPY.site.teacherCta}
      </button>
    </p>
  );
}

/**
 * Section rhythm, in one place.
 *
 * Tight horizontally on a phone — 20px each side of a 390px screen is already a tenth of the
 * width — and generous vertically, because scrolling is cheap and cramped sections are what make a
 * page feel like a form.
 */
const SECTION = "px-lg py-xxl md:px-xl md:py-[72px]";
const INNER = "mx-auto max-w-[1040px]";
const NARROW = "mx-auto max-w-[660px]";

/* ── Hero ──────────────────────────────────────────────────────────────────────────────────── */

function Hero({
  onStart,
  onAbout,
  onApply,
}: {
  onStart: () => void;
  onAbout: () => void;
  /** Null when somebody is already signed in — see `LandingPage`. */
  onApply: (() => void) | null;
}) {
  return (
    <section className={`${SECTION} pt-lg`}>
      <div className={`${INNER} grid items-center gap-xl md:grid-cols-[1.05fr_0.95fr] md:gap-xxl`}>
        {/* Text first in the DOM: on a phone this is what should be under the thumb on load. */}
        <div>
          <span className="kd-rise mb-md inline-block rounded-pill bg-grape/12 px-md py-1.5 text-caption text-grape">
            {COPY.site.heroEyebrow}
          </span>

          {/*
            * Fluid rather than fixed. `text-hero` is 34px, which is right in a card and small for
            * the first thing on a landing page. `clamp` is comfortable at 360px and commanding at
            * 1040 with no breakpoint to maintain — and the lower bound is the number that matters,
            * because it is the one almost everybody sees.
            */}
          <h1
            className="kd-rise-1 mb-md text-[clamp(30px,8vw,52px)] leading-[1.25] font-extrabold tracking-[-0.02em]"
          >
            {COPY.site.heroTitle}
          </h1>

          <p
            className="kd-rise-2 mb-xl max-w-[460px] text-body text-muted"
          >
            {COPY.site.heroLead}
          </p>

          {/* Full-width buttons on a phone: a thumb crossing a 390px screen should not have to aim. */}
          <div
            className="kd-rise-3 flex flex-col gap-md sm:flex-row sm:items-center"
          >
            <button onClick={onStart} className={PRIMARY}>
              {COPY.site.heroCta}
            </button>
            <button onClick={onAbout} className={SECONDARY}>
              {COPY.site.heroSecondary}
            </button>
          </div>

          {onApply && <ApplyPrompt onApply={onApply} />}
        </div>

        <HeroCreatures />
      </div>
    </section>
  );
}

/**
 * The creatures, as a composition rather than a row of badges.
 *
 * Positions are percentages of the container, so one arrangement works at every width with no
 * media query. Sizes vary on purpose: a group of identically-sized shapes reads as a chart, and a
 * group with a clear largest member reads as a scene with a subject.
 */
function HeroCreatures() {
  return (
    <div
      aria-hidden
      /**
       * `pb-[82%]` with `h-0`, not `aspect-[1/0.82]`.
       *
       * `aspect-ratio` is the modern spelling and it is unsupported on old WebView — where it does
       * not degrade, it collapses the box to zero height and the whole composition disappears.
       * That is not a remote risk here: the shell is a WebView (ADR-007) and Play Store is
       * unavailable in Iran, so Android System WebView does not quietly update itself the way it
       * does elsewhere. Percentage padding resolves against WIDTH and has worked everywhere for
       * twenty years.
       */
      className="relative mx-auto h-0 w-full max-w-[460px] pb-[82%]"
    >
      {/* A soft disc behind them, so the group reads as one object and not as scattered dots. */}
      <div className="kd-hero-glow absolute top-[8%] right-[8%] bottom-[8%] left-[8%] rounded-full" />
      {HERO_CREATURES.map((c) => (
        <span key={c.kind} className={`kd-drift absolute ${c.place} ${c.delay}`}>
          <span
            className={`relative grid h-0 w-full place-items-center rounded-full bg-card pb-[100%] ${ACCENT[c.accent].shadow}`}
          >
            <span className="absolute inset-0 grid place-items-center">
              <span className="grid w-[62%] place-items-center">
                <Creature kind={c.kind} accent={c.accent} size={64} />
              </span>
            </span>
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Whole class strings, not numbers.
 *
 * Position, size and delay are all design choices with exactly five values, so none of them needs
 * computing — and a class assembled at runtime is a class Tailwind never emits.
 */
const HERO_CREATURES = [
  { kind: "firefly", accent: "sunny", place: "start-[30%] top-[8%] w-[40%]", delay: "kd-delay-0" },
  { kind: "owl", accent: "grape", place: "start-[4%] top-[34%] w-[30%]", delay: "kd-delay-3" },
  { kind: "fish", accent: "turquoise", place: "start-[66%] top-[40%] w-[30%]", delay: "kd-delay-6" },
  { kind: "penguin", accent: "sky", place: "start-[22%] top-[62%] w-[26%]", delay: "kd-delay-1" },
  { kind: "chameleon", accent: "mint", place: "start-[52%] top-[70%] w-[24%]", delay: "kd-delay-4" },
] as const satisfies readonly { kind: string; accent: AccentName; place: string; delay: string }[];

/* ── The games ─────────────────────────────────────────────────────────────────────────────── */

function GameShelf() {
  // The badge counts the whole shelf; the grid below shows only a curated few of them (see
  // `featuredShelfEntries`). Keeping the count off the full list and the cards off the curated one is
  // what stops the badge from ever overstating — or undercounting — what actually ships.
  const totalCount = shelfEntries().length;
  const entries = featuredShelfEntries();
  return (
    <section className={`${SECTION} bg-card/60`}>
      <div className={INNER}>
        <SectionHead
          title={COPY.site.gamesTitle}
          lead={COPY.site.gamesLead}
          badge={fill(COPY.site.gamesCount, { count: fa(totalCount) })}
        />
        {/* Two columns on the narrowest phone, growing on their own from there — no breakpoints. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(148px,1fr))] gap-md">
          {entries.map((entry, i) => {
            const look = familyLook(entry.family);
            const accent = familyClasses(entry.family);
            return (
              <article
                key={entry.family}
                className="grid place-items-center gap-sm rounded-lg bg-card px-sm py-lg text-center shadow-card"
              >
                {/* Staggered, so a grid of a dozen creatures does not bob in lockstep. */}
                <span className={`grid size-[76px] place-items-center rounded-full ${accent.disc}`}>
                  <Creature kind={look.creature} accent={look.accent} size={52} step={i} />
                </span>
                <h3 className="text-small font-bold">{entry.label}</h3>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── What it shows ─────────────────────────────────────────────────────────────────────────── */

function Values() {
  return (
    <section className={SECTION}>
      <div className={INNER}>
        <SectionHead title={COPY.site.valueTitle} />
        <div className="grid gap-lg md:grid-cols-3">
          <ValueCard
            creature="cat"
            accent="watermelon"
            title={COPY.site.values.learntTitle}
            body={COPY.site.values.learntBody}
          />
          <ValueCard
            creature="robot"
            accent="turquoise"
            title={COPY.site.values.compareTitle}
            body={COPY.site.values.compareBody}
          />
          <ValueCard
            creature="turtle"
            accent="grape"
            title={COPY.site.values.nextTitle}
            body={COPY.site.values.nextBody}
          />
        </div>
      </div>
    </section>
  );
}

function ValueCard({
  creature,
  accent,
  title,
  body,
}: {
  creature: Parameters<typeof Creature>[0]["kind"];
  accent: AccentName;
  title: string;
  body: string;
}) {
  const look = ACCENT[accent];
  return (
    // A tinted wash rather than white: three white cards on a cream page read as a list, three
    // coloured ones read as a set of ideas.
    <article className={`rounded-xl border border-ink/8 p-xl ${look.wash}`}>
      <span
        className={`mb-lg grid size-[84px] place-items-center rounded-full bg-card ${look.shadow}`}
      >
        <Creature kind={creature} accent={accent} size={56} />
      </span>
      <h3 className="mb-sm text-title">{title}</h3>
      <p className="text-small text-muted">{body}</p>
    </article>
  );
}

/* ── What it does not say ──────────────────────────────────────────────────────────────────── */

function Honesty() {
  return (
    <section className={SECTION}>
      {/* Ink rather than grey. This is the page's argument, and the previous version set it in a
          pale panel that read as a disclaimer — the one thing it must not look like. Inverted, it
          is the section a reader stops at. */}
      <div className={`${INNER} kd-panel-ink rounded-xl px-lg py-xxl text-cream`}>
        <div className={NARROW}>
          <h2 className="mb-lg text-[clamp(24px,6vw,34px)] leading-[1.3] font-extrabold">
            {COPY.site.honestTitle}
          </h2>
          <Passage text={COPY.site.honestBody} className="text-body text-cream/82" />
        </div>
      </div>
    </section>
  );
}

/* ── Promises ──────────────────────────────────────────────────────────────────────────────── */

/**
 * What this page has instead of award badges.
 *
 * The category's landing pages are built on trust markers we do not have: certifications, star
 * ratings, "200M downloads", testimonials. Borrowing the FORM of that section while inventing the
 * content would be the precise dishonesty the product refuses. Every line here is instead something a
 * teacher can verify themselves within ten minutes of signing up.
 *
 * **"No comparison" used to be one of these and is gone.** It was a promise the old product kept and
 * this one breaks on purpose: comparing children inside one class is the feature the pivot was asked
 * for (ADR-011). Leaving the promise on the page while shipping the comparison would have been the
 * one dishonesty this file cannot afford, so what stands in its place is the boundary that still
 * holds — the comparison never leaves the room.
 */
function Promises() {
  const items = [
    { title: COPY.site.promises.adsTitle, body: COPY.site.promises.adsBody, accent: "mint" },
    {
      title: COPY.site.promises.scopeTitle,
      body: COPY.site.promises.scopeBody,
      accent: "sky",
    },
    { title: COPY.site.promises.scoreTitle, body: COPY.site.promises.scoreBody, accent: "sunny" },
    {
      title: COPY.site.promises.labelTitle,
      body: COPY.site.promises.labelBody,
      accent: "watermelon",
    },
  ] as const satisfies readonly { title: string; body: string; accent: AccentName }[];

  return (
    <section className={`${SECTION} bg-card/60`}>
      <div className={INNER}>
        <SectionHead title={COPY.site.promisesTitle} />
        <div className="grid gap-md sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <article key={item.title} className="rounded-lg bg-card p-lg shadow-card">
              <NoSign accent={item.accent} />
              <h3 className="mt-md mb-xs text-heading">{item.title}</h3>
              <p className="text-small text-muted">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/** A crossed-out circle. An SVG, because a character glyph would inherit bidi behaviour in RTL. */
function NoSign({ accent }: { accent: AccentName }) {
  // `currentColor` and a text utility, so the mark takes its colour from a class like everything
  // else. `fill-current` with an opacity utility gives the wash without a computed rgba.
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 24 24"
      aria-hidden
      className={ACCENT[accent].text}
    >
      <circle cx="12" cy="12" r="9" className="fill-current opacity-16" />
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M6.8 6.8 L17.2 17.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* ── The loop ──────────────────────────────────────────────────────────────────────────────── */

/**
 * The four steps of the teaching loop.
 *
 * Replaces a three-step "how it works" that described signing up and waiting. The loop IS the product
 * now, so it goes on the page as a loop — a teacher deciding whether this is worth an afternoon needs
 * to see where their own work fits in it.
 */
function HowItWorks() {
  const steps = [
    `${COPY.site.loop.oneTitle} — ${COPY.site.loop.oneBody}`,
    `${COPY.site.loop.twoTitle} — ${COPY.site.loop.twoBody}`,
    `${COPY.site.loop.threeTitle} — ${COPY.site.loop.threeBody}`,
    `${COPY.site.loop.fourTitle} — ${COPY.site.loop.fourBody}`,
  ];
  return (
    <section className={SECTION}>
      <div className={INNER}>
        <SectionHead title={COPY.site.loopTitle} />
        <ol className={`${NARROW} grid list-none gap-lg p-0`}>
          {steps.map((step, i) => (
            <li key={i} className="relative flex items-start gap-lg">
              {/* A rail joining the markers, so three steps read as a sequence rather than a list.
                  Drawn on all but the last, and only from the marker downwards. */}
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className="absolute start-[21px] top-[46px] -bottom-lg w-0.5 rounded-full bg-grape/22"
                />
              )}
              <span className="z-1 grid size-11 shrink-0 place-items-center rounded-pill bg-grape/15 text-heading text-grape">
                {fa(i + 1)}
              </span>
              <p className="pt-1.5 text-body">{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ── Close ─────────────────────────────────────────────────────────────────────────────────── */

function Closing({
  onStart,
  onApply,
}: {
  onStart: () => void;
  onApply: (() => void) | null;
}) {
  return (
    <section className={SECTION}>
      <div className={`${INNER} kd-panel-close rounded-xl px-lg py-xxl text-center`}>
        <h2 className="mb-sm text-[clamp(24px,6vw,34px)] leading-[1.3] font-extrabold">
          {COPY.site.closingTitle}
        </h2>
        <p className="mx-auto mb-xl max-w-[460px] text-body text-muted">{COPY.site.closingBody}</p>
        {/* Full width on a phone, contained on a wider screen — the same rule as the hero. */}
        <div className="flex flex-col sm:flex-row sm:justify-center">
          <button onClick={onStart} className={PRIMARY}>
            {COPY.site.heroCta}
          </button>
        </div>

        {onApply && <ApplyPrompt onApply={onApply} />}
      </div>
    </section>
  );
}

/* ── Shared pieces ─────────────────────────────────────────────────────────────────────────── */

function SectionHead({ title, lead, badge }: { title: string; lead?: string; badge?: string }) {
  return (
    <header className="mb-xl text-center">
      {badge && (
        <span className="mb-sm inline-block rounded-pill bg-turquoise/20 px-md py-1 text-caption text-ink">
          {badge}
        </span>
      )}
      <h2 className="text-[clamp(23px,5.6vw,32px)] leading-[1.3] font-extrabold">{title}</h2>
      {lead && <p className="mx-auto mt-sm max-w-[440px] text-small text-muted">{lead}</p>}
    </header>
  );
}

/**
 * `min-h-[76px]` is `touchMin()`, and it is a measurement rule rather than a comfort one: a
 * mis-aimed tap recorded as a wrong answer is a motor-skill artefact scored as a cognitive one.
 * It applies here too — a parent tapping the wrong button is a parent who does not sign up.
 */
const PRIMARY =
  "min-h-[76px] rounded-pill bg-grape px-xxl py-md text-heading text-card sm:w-auto";
const SECONDARY =
  "min-h-[76px] rounded-pill bg-ink/6 px-xl py-md text-heading font-bold text-ink sm:w-auto";


